import React, { useRef, useState, useEffect } from 'react';
import io from 'socket.io-client';

const App = () => {
  // Refs
  const localVideoRef = useRef(null);
  const socketRef = useRef();
  const peerConnectionsRef = useRef({});
  const localStreamRef = useRef(null);
  
  // State
  const [roomId, setRoomId] = useState('');
  const [joined, setJoined] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [micActive, setMicActive] = useState(true);
  const [cameraActive, setCameraActive] = useState(true);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [userId] = useState(`user-${Math.random().toString(36).substr(2, 8)}`);

  // Initialize socket connection
  useEffect(() => {
    socketRef.current = io('https://meetingapi.anjgreybox.in');

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      stopLocalStream();
    };
  }, []);

  const startLocalStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      localStreamRef.current = stream;
      localVideoRef.current.srcObject = stream;
      return true;
    } catch (err) {
      setMediaError('Could not access camera/microphone');
      console.error('Media error:', err);
      return false;
    }
  };

  const stopLocalStream = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
  };

  const createPeerConnection = (remoteUserId) => {
    if (peerConnectionsRef.current[remoteUserId]) {
      return peerConnectionsRef.current[remoteUserId];
    }

    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    });

    // Add local stream tracks if available
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        socketRef.current.emit('ice-candidate', {
          target: remoteUserId,
          candidate: event.candidate
        });
      }
    };

    pc.ontrack = (event) => {
      setRemoteUsers(prevUsers => {
        // Check if we already have this user's stream
        const userExists = prevUsers.some(user => user.id === remoteUserId);
        if (!userExists) {
          return [...prevUsers, { 
            id: remoteUserId, 
            stream: event.streams[0] 
          }];
        }
        return prevUsers;
      });
    };

    pc.onnegotiationneeded = async () => {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socketRef.current.emit('offer', {
          target: remoteUserId,
          sdp: offer
        });
      } catch (err) {
        console.error('Error creating offer:', err);
      }
    };

    peerConnectionsRef.current[remoteUserId] = pc;
    return pc;
  };

  const handleOffer = async ({ sender, sdp }) => {
    const pc = createPeerConnection(sender);
    await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    socketRef.current.emit('answer', {
      target: sender,
      sdp: answer
    });
  };

  const handleAnswer = async ({ sender, sdp }) => {
    const pc = peerConnectionsRef.current[sender];
    if (pc) {
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    }
  };

  const handleIceCandidate = async ({ sender, candidate }) => {
    const pc = peerConnectionsRef.current[sender];
    if (pc && candidate) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error('Error adding ICE candidate:', err);
      }
    }
  };

  const handleUserDisconnected = (disconnectedUserId) => {
    const pc = peerConnectionsRef.current[disconnectedUserId];
    if (pc) {
      pc.close();
      delete peerConnectionsRef.current[disconnectedUserId];
      setRemoteUsers(prevUsers => 
        prevUsers.filter(user => user.id !== disconnectedUserId)
      );
    }
  };

  const setupSocketListeners = () => {
    socketRef.current.on('existing-users', (userIds) => {
      userIds.forEach(id => {
        if (id !== userId) {
          createPeerConnection(id);
        }
      });
    });

    socketRef.current.on('user-connected', (newUserId) => {
      if (newUserId !== userId) {
        createPeerConnection(newUserId);
      }
    });

    socketRef.current.on('offer', handleOffer);
    socketRef.current.on('answer', handleAnswer);
    socketRef.current.on('ice-candidate', handleIceCandidate);
    socketRef.current.on('user-disconnected', handleUserDisconnected);
  };

  const toggleMic = () => {
    if (localStreamRef.current) {
      const newState = !micActive;
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = newState;
      });
      setMicActive(newState);
    }
  };

  const toggleCamera = () => {
    if (localStreamRef.current) {
      const newState = !cameraActive;
      localStreamRef.current.getVideoTracks().forEach(track => {
        track.enabled = newState;
      });
      setCameraActive(newState);
    }
  };

  const joinRoom = async () => {
    try {
      const success = await startLocalStream();
      if (!success) return;

      setupSocketListeners();

      socketRef.current.emit('join-room', {
        roomId: roomId || 'default-room',
        userId
      });

      setJoined(true);
    } catch (err) {
      console.error('Failed to join room:', err);
      setMediaError(err.message);
    }
  };

  const leaveRoom = () => {
    // Close all peer connections
    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    peerConnectionsRef.current = {};
    
    // Stop local stream
    stopLocalStream();
    
    // Reset state
    setRemoteUsers([]);
    setJoined(false);
    
    // Notify server we're leaving
    socketRef.current.emit('leave-room', {
      roomId: roomId || 'default-room',
      userId
    });
  };

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      {/* Video Grid */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 20,
        marginBottom: 80
      }}>
        {/* Local Video */}
        <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
          <video
            ref={localVideoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: '100%',
              height: 'auto',
              display: joined ? 'block' : 'none',
              backgroundColor: '#000'
            }}
          />
          <div style={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 14
          }}>
            You (Me)
          </div>
        </div>

        {/* Remote Videos */}
        {remoteUsers.map(({ id, stream }) => (
          <div key={id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
            <video
              autoPlay
              playsInline
              ref={el => {
                if (el && !el.srcObject) {
                  el.srcObject = stream;
                }
              }}
              style={{
                width: '100%',
                height: 'auto',
                backgroundColor: '#000'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 14
            }}>
              User {id.slice(-4)}
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      {joined ? (
        <div style={{
          position: 'fixed',
          bottom: 20,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 10,
          zIndex: 100
        }}>
          <button
            onClick={toggleMic}
            style={{
              padding: '10px 20px',
              backgroundColor: micActive ? '#4CAF50' : '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 16
            }}
          >
            {micActive ? 'Mute' : 'Unmute'}
          </button>
          <button
            onClick={toggleCamera}
            style={{
              padding: '10px 20px',
              backgroundColor: cameraActive ? '#4CAF50' : '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 16
            }}
          >
            {cameraActive ? 'Stop Video' : 'Start Video'}
          </button>
          <button
            onClick={leaveRoom}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 16
            }}
          >
            Leave Room
          </button>
        </div>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          marginTop: 40,
          maxWidth: 500,
          margin: '0 auto'
        }}>
          <h2 style={{ marginBottom: 30 }}>Video Chat App</h2>
          <div style={{ marginBottom: 20 }}>
            <input
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              placeholder="Enter Room ID"
              style={{
                padding: '12px 15px',
                width: '100%',
                borderRadius: 4,
                border: '1px solid #ddd',
                fontSize: 16
              }}
            />
          </div>
          <button
            onClick={joinRoom}
            style={{
              padding: '12px 30px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 16,
              width: '100%'
            }}
          >
            Join Room
          </button>
          {mediaError && (
            <div style={{ 
              color: '#f44336', 
              marginTop: 20,
              padding: 10,
              backgroundColor: '#ffebee',
              borderRadius: 4
            }}>
              {mediaError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default App;